import * as React from "react";
import {Button} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {WithApi, WithApiProperties} from "../../util/WithApi";
import {ActionBar} from "../actionbar/ActionBar";
import {LcdIcon} from "../icon/LcdIcon";
import {BasicColumnFactory, ColumnWidth} from "../table/ControlRoomColumns";
import {ControlRoomTable} from "../table/ControlRoomTable";
import {ChooseFileDialog} from "./ChooseFileDialog";
import {ChooseFileFormItem, ChooseFileFormSourceType, ChooseMode} from "./ChooseFileForm";

interface FileListProps {
  source?: ChooseFileFormSourceType;
  files: string[];
  onChange?: (event: { target: { name: "files"; value: string[]; } }) => void;
  onAdd?: (addedFiles: ChooseFileFormItem[], files: string[]) => void;
  onRemove?: (removedFiles: string[], files: string[]) => void;
  tableClassName?: string;
}

interface FileListState {
  selectedItems: string[];
  isChooseFilesDialogOpen: boolean;
}

class FilesColumnFactory extends BasicColumnFactory<string> {

  constructor() {
    super({
      filePath: {
        header: <FormattedMessage id="studio.settings.data-roots-directory-column.path-header" defaultMessage="Path"/>,
        cellContent: (path) => path,
        cellTooltip: (path) => path,
        columnWidth: ColumnWidth.flex(1),
      },
    });
  }
}

const MESSAGES = defineMessages({
  chooseFilesDialogTitle: {
    id: "studio.files-list.choose-file-dialog-title",
    defaultMessage: "Choose files",
  },
  chooseFilesDialogButtonText: {
    id: "studio.files-list.choose-file-dialog-button-text",
    defaultMessage: "Select",
  },
});

const getFilesItemsListDimensions = (parent: HTMLElement) => {
  const paddingLeft = parseFloat(getComputedStyle(parent).paddingLeft) || 0;
  const paddingRight = parseFloat(getComputedStyle(parent).paddingRight) || 0;

  return {
    width: parent.clientWidth - paddingLeft - paddingRight,
    height: 350,
  };
};

const FileItemsListComponent = ControlRoomTable(new FilesColumnFactory(), getFilesItemsListDimensions);

class FileListComponent extends React.Component<FileListProps & WithApiProperties & InjectedIntlProps, FileListState> {

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      isChooseFilesDialogOpen: false,
    };
  }

  render() {
    const {intl, files, tableClassName} = this.props;
    const {selectedItems} = this.state;

    return (
        <div className="drawer-panel-full-width">
          <ActionBar>
            <Button onClick={this.openChooseFilesDialog}>
              <LcdIcon icon={"plus"}/>
              <FormattedMessage id="studio.files-list.add-button" defaultMessage="Add"/>
            </Button>
            <Button onClick={this.removeSelectedItems} disabled={selectedItems.length === 0}>
              <LcdIcon icon={"delete-circle"}/>
              <FormattedMessage id="studio.files-list.remove-button" defaultMessage="Remove"/>
            </Button>
          </ActionBar>
          <FileItemsListComponent
              className={tableClassName}
              isPaged={false}
              intl={intl}
              data={files}
              isAllDataLoaded={true}
              loadData={() => undefined}
              onRowSelect={this.handleRowSelect}
              canMultiSelect={true}
              clearSelection={selectedItems.length === 0}
              noItemsFoundMessage={<FormattedMessage
                  id="studio.files-list.empty"
                  defaultMessage="No files"/>}
          />
          {this.renderChooseFileDialog()}
        </div>
    );
  }

  private renderChooseFileDialog() {
    const {intl, source} = this.props;
    const {isChooseFilesDialogOpen} = this.state;
    return (
        <ChooseFileDialog chooseMode={ChooseMode.FILES}
                          canMultiSelect={true}
                          handleMultiChoose={this.handleChooseFiles}
                          show={isChooseFilesDialogOpen}
                          onHide={this.closeChooseFileDialog}
                          onChoosed={this.closeChooseFileDialog}
                          initialPath={source === ChooseFileFormSourceType.S3 ? "/" : "C:\\"}
                          title={intl.formatMessage(MESSAGES.chooseFilesDialogTitle)}
                          buttonText={intl.formatMessage(MESSAGES.chooseFilesDialogButtonText)}
                          source={this.props.source}
        />
    );
  }

  private handleChooseFiles = (filePaths: ChooseFileFormItem[]) => {
    const {onChange, onAdd, files} = this.props;

    const newFiles = filePaths.map(f => f.path);
    const updatedFiles = [...files.filter(f => !newFiles.includes(f)), ...newFiles];

    onChange && onChange({target: {name: "files", value: updatedFiles}});
    onAdd && onAdd(filePaths, updatedFiles);

    return Promise.resolve();
  };

  updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));
  private openChooseFilesDialog = () => {
    this.updateState({
      isChooseFilesDialogOpen: true,
    });
  };

  private closeChooseFileDialog = () => {
    this.updateState({
      isChooseFilesDialogOpen: false,
    });
  };

  private handleRowSelect = (selectedItems: string[]) => {
    this.updateState({
      selectedItems,
    })
  }

  private removeSelectedItems = () => {
    const {onChange, onRemove, files} = this.props;
    const {selectedItems} = this.state;
    const updatedFiles = files.filter(f => !selectedItems.includes(f));

    onChange && onChange({target: {name: "files", value: updatedFiles}});
    onRemove && onRemove(selectedItems, updatedFiles);
    this.updateState({
      selectedItems: [],
    });
  };
}

export const FileList = injectIntl(WithApi(FileListComponent));